import {
  ArrowDownLeft,
  ArrowUpRight,
  ArrowUpRightFromSquare,
  CheckCircle2,
  Clock3,
  MessageSquareText,
  ShieldAlert,
  UserRound,
} from "lucide-react";

import { TradeStatus, TradeType, type Trade } from "@/interfaces/trade";

import { useAppSelector } from "@/store/hooks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { cn, normalizeCurrency } from "@/lib/utils";
import { formatCurrency } from "@/utils";
import { format } from "timeago.js";

import { useNavigate } from "react-router-dom";
import type { Dispatch, SetStateAction } from "react";

import { tradeSocket } from "@/services/tradeSocket";

interface Props {
  trade: Trade;
  selectedTrade: Trade | null;
  setSelectedTrade: Dispatch<SetStateAction<Trade | null>>;
  openReview: boolean;
  setOpenReview: Dispatch<SetStateAction<boolean>>;
}

interface StatusConfig {
  label: string;
  badgeClass: string;
  dotClass: string;
  buttonClass: string;
}

const getTradeStatusConfig = (status: TradeStatus | string): StatusConfig => {
  switch (String(status).toUpperCase()) {
    case "PENDING":
      return {
        label: "Awaiting Payment",
        badgeClass:
          "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400",
        dotClass: "bg-amber-500",
        buttonClass: "bg-amber-600 text-white hover:bg-amber-700",
      };

    case "PAID":
      return {
        label: "Payment Sent",
        badgeClass:
          "border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-400",
        dotClass: "bg-blue-500",
        buttonClass: "bg-blue-600 text-white hover:bg-blue-700",
      };

    case "DISPUTED":
      return {
        label: "Disputed",
        badgeClass:
          "border-orange-500/30 bg-orange-500/10 text-orange-700 dark:text-orange-400",
        dotClass: "bg-orange-500",
        buttonClass: "bg-orange-600 text-white hover:bg-orange-700",
      };

    case "UNDER_REVIEW":
      return {
        label: "Under Review",
        badgeClass:
          "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-400",
        dotClass: "bg-violet-500",
        buttonClass: "bg-violet-600 text-white hover:bg-violet-700",
      };

    case "RELEASED":
      return {
        label: "Completed",
        badgeClass:
          "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
        dotClass: "bg-emerald-500",
        buttonClass: "bg-emerald-600 text-white hover:bg-emerald-700",
      };

    case "AUTO_RELEASED":
      return {
        label: "Auto Released",
        badgeClass:
          "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
        dotClass: "bg-emerald-500",
        buttonClass: "bg-emerald-600 text-white hover:bg-emerald-700",
      };

    case "REFUNDED":
      return {
        label: "Refunded",
        badgeClass:
          "border-slate-500/30 bg-slate-500/10 text-slate-600 dark:text-slate-400",
        dotClass: "bg-slate-500",
        buttonClass: "bg-slate-600 text-white hover:bg-slate-700",
      };

    case "CANCELLED":
      return {
        label: "Cancelled",
        badgeClass:
          "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-400",
        dotClass: "bg-red-500",
        buttonClass: "bg-red-600 text-white hover:bg-red-700",
      };

    case "EXPIRED":
      return {
        label: "Expired",
        badgeClass:
          "border-zinc-500/30 bg-zinc-500/10 text-zinc-600 dark:text-zinc-400",
        dotClass: "bg-zinc-500",
        buttonClass: "bg-zinc-600 text-white hover:bg-zinc-700",
      };

    default:
      return {
        label: String(status).replace(/_/g, " "),
        badgeClass: "border-border bg-muted text-muted-foreground",
        dotClass: "bg-muted-foreground",
        buttonClass: "bg-primary text-primary-foreground hover:bg-primary/90",
      };
  }
};

const getTradeAction = (trade: Trade, currentUserId: string) => {
  const isBuyer = trade.buyerId === currentUserId;

  return {
    isBuyer,
    label: isBuyer ? "Bought" : "Sold",
    role: isBuyer ? "Buyer" : "Seller",
    icon: isBuyer ? ArrowDownLeft : ArrowUpRight,

    badgeClass: isBuyer
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
      : "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-400",

    iconClass: isBuyer
      ? "text-emerald-600 dark:text-emerald-400"
      : "text-red-600 dark:text-red-400",
  };
};

const completedStatuses: string[] = [
  TradeStatus.RELEASED,
  TradeStatus.AUTO_RELEASED,
];

const TradeCard = ({
  trade,
  selectedTrade,
  setSelectedTrade,
  openReview,
  setOpenReview,
}: Props) => {
  const { user } = useAppSelector((state) => state.auth);

  const userId = user?.id ?? "";
  const navigate = useNavigate();

  const status = getTradeStatusConfig(trade.status);
  const tradeAction = getTradeAction(trade, userId);

  const isBuyer = trade.buyerId === userId;
  const isSeller = trade.sellerId === userId;

  const counterpartyId = isBuyer ? trade.sellerId : trade.buyerId;

  const ActionIcon = tradeAction.icon;

  const assetCurrency = trade.advertisement?.asset?.currency ?? "";

  const fiatSymbol = trade.advertisement?.fiatCurrency?.symbol ?? "";

  const assetAmount = Number(trade.amount);
  const fiatAmount = Number(trade.fiatAmount);

  const isCompleted = completedStatuses.some(
    (completedStatus) => String(completedStatus) === String(trade.status),
  );

  const isTerminal = ["CANCELLED", "EXPIRED", "REFUNDED"].includes(
    String(trade.status).toUpperCase(),
  );

  const canLeaveReview = (() => {
    const isParticipant = isBuyer || isSeller;

    const hasReviewed =
      trade.reviews?.some((review) => review.userId === userId) ?? false;

    return isParticipant && isCompleted && !hasReviewed;
  })();

  const handleReview = () => {
    setSelectedTrade(trade);
    setOpenReview(true);
  };

  const handleNavigate = (tradeId: string) => {
    tradeSocket.connect();
    tradeSocket.joinTrade(tradeId);
    navigate(`/p2p/${tradeId}`);
  };

  const handleViewCounterparty = () => {
    if (!counterpartyId) return;

    navigate(`/p2p/merchant/${counterpartyId}`);
  };

  return (
    <article
      className={cn(
        "group flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-card",
        "transition-all duration-200",
        "hover:border-primary/40 hover:shadow-md hover:shadow-primary/5",
      )}
    >
      {/* Card header */}
      <div className="flex items-start justify-between gap-3 border-b border-border/70 px-4 py-4 sm:px-5">
        <div className="flex min-w-0 items-start gap-3">
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
              isBuyer ? "bg-emerald-500/10" : "bg-red-500/10",
            )}
          >
            <ActionIcon className={cn("h-5 w-5", tradeAction.iconClass)} />
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold tracking-tight" title={trade.id}>
                Trade #{trade.id.slice(0, 8).toUpperCase()}
              </h3>

              <Badge
                variant="outline"
                className={cn("shrink-0 font-medium", tradeAction.badgeClass)}
              >
                {tradeAction.label}
              </Badge>
            </div>

            <p className="mt-1 text-xs text-muted-foreground">
              You participated as the {tradeAction.role.toLowerCase()}
            </p>
          </div>
        </div>

        <Badge
          variant="outline"
          className={cn(
            "max-w-[45%] shrink-0 gap-1.5 whitespace-normal text-center",
            status.badgeClass,
          )}
        >
          <span
            className={cn("h-1.5 w-1.5 shrink-0 rounded-full", status.dotClass)}
          />
          {status.label}
        </Badge>
      </div>

      {/* Trade information */}
      <div className="flex-1 space-y-4 p-4 sm:p-5">
        {/* Asset and amount */}
        <div className="grid grid-cols-2 gap-4">
          <div className="min-w-0">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Asset
            </p>

            <p className="truncate text-sm font-semibold">
              {assetCurrency ? normalizeCurrency(assetCurrency) : "—"}
            </p>
          </div>

          <div className="min-w-0 text-right">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Amount
            </p>

            <p className="truncate text-sm font-semibold tabular-nums">
              {Number.isFinite(assetAmount) ? formatCurrency(assetAmount) : "—"}
            </p>
          </div>
        </div>

        <div className="h-px bg-border/70" />

        {/* Fiat value and time */}
        <div className="grid grid-cols-2 gap-4">
          <div className="min-w-0">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Trade value
            </p>

            <p className="break-words text-sm font-semibold tabular-nums">
              {Number.isFinite(fiatAmount)
                ? `${fiatSymbol} ${formatCurrency(fiatAmount)}`
                : "—"}
            </p>
          </div>

          <div className="min-w-0 text-right">
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Created
            </p>

            <p
              className="truncate text-sm font-medium text-muted-foreground"
              title={String(trade.createdAt ?? "")}
            >
              {trade.createdAt ? format(trade.createdAt) : "—"}
            </p>
          </div>
        </div>

        {/* Trade reference */}
        <div className="flex items-center justify-between gap-2 rounded-lg bg-muted/50 px-3 py-2">
          <span className="text-xs text-muted-foreground">Reference</span>

          <span
            className="truncate font-mono text-xs text-foreground"
            title={trade.id}
          >
            {trade.id}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-auto space-y-2 border-t border-border/70 p-4 sm:p-5">
        <Button
          type="button"
          onClick={() => handleNavigate(trade.id)}
          className={cn("h-11 w-full gap-2 font-semibold", status.buttonClass)}
        >
          {isCompleted ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : isTerminal ? (
            <ShieldAlert className="h-4 w-4" />
          ) : (
            <ArrowUpRightFromSquare className="h-4 w-4" />
          )}

          {isCompleted
            ? "View Trade"
            : isTerminal
              ? "View Details"
              : "Open Trade"}
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={handleViewCounterparty}
          disabled={!counterpartyId}
          className="h-11 w-full gap-2"
        >
          <UserRound className="h-4 w-4" />

          {isBuyer ? "View Seller" : "View Buyer"}
        </Button>

        {canLeaveReview && (
          <Button
            type="button"
            variant="outline"
            onClick={handleReview}
            className="h-11 w-full gap-2 border-primary/30 text-primary hover:bg-primary/5 hover:text-primary"
          >
            <MessageSquareText className="h-4 w-4" />
            Leave Review
          </Button>
        )}
      </div>
    </article>
  );
};

export default TradeCard;
